import { useState } from 'react';
import { 
  Play, Eye, RotateCcw, BarChart3, User, Users, Store, 
  UserCog, ChevronRight, ChevronDown, Shield, AlertTriangle,
  CheckCircle2, Copy, ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

/**
 * Owner Demo Guide
 * 
 * Accessible only to authorized Admin users.
 * Provides complete instructions for investor demonstrations.
 * 
 * SECURITY:
 * - No production credentials exposed
 * - No authentication bypass
 * - Demo data only in TEST environment
 * - PILOT_MODE=true required
 */
export default function OwnerDemoGuide({ onClose }) {
  const [expandedSection, setExpandedSection] = useState('access');
  
  const sections = [
    {
      id: 'access',
      title: 'Accessing Investor Demo',
      icon: Play,
      content: (
        <div className="space-y-4">
          <div className="bg-slate-700/50 rounded-lg p-4">
            <p className="text-sm text-slate-300 mb-2">
              <strong>URL:</strong> <code className="bg-slate-800 px-2 py-1 rounded">/admin/investor-demo</code>
            </p>
            <p className="text-sm text-slate-300 mb-2">
              <strong>Login Required:</strong> YES (Admin account)
            </p>
            <p className="text-sm text-slate-300">
              <strong>Admin Account:</strong> Use your authorized admin credentials
            </p>
          </div>
          
          <div className="bg-purple-900/30 border border-purple-700/50 rounded-lg p-4">
            <h4 className="text-purple-300 font-medium mb-2">How to Access:</h4>
            <ol className="text-sm text-slate-300 space-y-2">
              <li>1. Go to <strong>/admin</strong></li>
              <li>2. Login with your admin email and password</li>
              <li>3. Complete MFA/TOTP if enabled</li>
              <li>4. Click <strong>"Investor Demo"</strong> card on the dashboard</li>
            </ol>
          </div>
        </div>
      )
    },
    {
      id: 'guided',
      title: 'Start 5-Minute Demo (Guided)',
      icon: Play,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            The guided demo walks investors through the complete PeCash transaction flow in 13 steps.
          </p>
          
          <div className="bg-slate-700/50 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Demo Story:</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-400">Customer</p>
                <p className="text-white font-medium">Jean (USA)</p>
              </div>
              <div>
                <p className="text-slate-400">Beneficiary</p>
                <p className="text-white font-medium">María Rodríguez (DR)</p>
              </div>
              <div>
                <p className="text-slate-400">Colmado</p>
                <p className="text-white font-medium">Colmado La Bendición</p>
              </div>
              <div>
                <p className="text-slate-400">Amount</p>
                <p className="text-white font-medium">$100 → $4.01 fee → $104.01 total</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <h4 className="text-white font-medium">13 Steps:</h4>
            <div className="bg-slate-800 rounded-lg p-3 text-xs space-y-1">
              <p><span className="text-blue-400">1-3 Customer:</span> Home → Buy for María → Review & Confirm</p>
              <p><span className="text-emerald-400">4 Beneficiary:</span> María's Home (sees availability)</p>
              <p><span className="text-amber-400">5-6 Colmado:</span> Nueva Compra → Request Approval (RD$2,850)</p>
              <p><span className="text-emerald-400">7 Beneficiary:</span> María approves exact amount</p>
              <p><span className="text-amber-400">8-9 Colmado:</span> COMPRA APROBADA → Delivery Progress</p>
              <p><span className="text-blue-400">10 Customer:</span> Activity (sees purchase)</p>
              <p><span className="text-amber-400">11 Merchant:</span> Economics (sees payable)</p>
              <p><span className="text-purple-400">12-13 Admin:</span> View Transaction → Unit Economics</p>
            </div>
          </div>
          
          <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-4">
            <h4 className="text-emerald-300 font-medium mb-2">How to Start:</h4>
            <ol className="text-sm text-slate-300 space-y-1">
              <li>1. Click <strong>"Start 5-Minute Demo"</strong></li>
              <li>2. Click <strong>"Next"</strong> to advance through steps</li>
              <li>3. Use <strong>"View [persona] Screen"</strong> to see the actual UI</li>
              <li>4. Use <strong>"Back"</strong> to revisit previous steps</li>
              <li>5. Click <strong>"Exit"</strong> to return to mode selection</li>
            </ol>
          </div>
        </div>
      )
    },
    {
      id: 'explore',
      title: 'Explore Demo (Free Navigation)',
      icon: Eye,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            Explore mode lets you freely switch between personas without following the guided steps.
          </p>
          
          <div className="bg-slate-700/50 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Available Personas:</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-600/50 flex items-center justify-center">
                  <User className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Customer (Jean)</p>
                  <p className="text-slate-400 text-xs">US sender who creates purchase access</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-600/50 flex items-center justify-center">
                  <Users className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Beneficiary (María)</p>
                  <p className="text-slate-400 text-xs">DR family member who approves purchases</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-600/50 flex items-center justify-center">
                  <Store className="w-4 h-4 text-amber-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Colmado (La Bendición)</p>
                  <p className="text-slate-400 text-xs">Local merchant processing purchases</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-600/50 flex items-center justify-center">
                  <UserCog className="w-4 h-4 text-purple-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Admin</p>
                  <p className="text-slate-400 text-xs">Platform administrator view</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-4">
            <h4 className="text-blue-300 font-medium mb-2">How to Explore:</h4>
            <ol className="text-sm text-slate-300 space-y-1">
              <li>1. Click <strong>"Explore Demo"</strong></li>
              <li>2. Click any persona button to switch views</li>
              <li>3. Use <strong>"Open [persona] Dashboard"</strong> to see actual UI</li>
              <li>4. Return anytime via <strong>"Back to Demo"</strong></li>
            </ol>
          </div>
        </div>
      )
    },
    {
      id: 'reset',
      title: 'Reset Demo',
      icon: RotateCcw,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            Reset restores the demo to its original state before each investor presentation.
          </p>
          
          <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4">
            <h4 className="text-amber-300 font-medium mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Important
            </h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Reset affects <strong>DEMO DATA ONLY</strong></li>
              <li>• Production data is never modified</li>
              <li>• Creates fresh $100 authorization</li>
              <li>• Starts new analytics tracking session</li>
            </ul>
          </div>
          
          <div className="bg-slate-700/50 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">How to Reset:</h4>
            <ol className="text-sm text-slate-300 space-y-1">
              <li>1. In Guided Demo, click <strong>"Reset"</strong> (top-right)</li>
              <li>2. In Explore mode, click <strong>"Reset Demo"</strong></li>
              <li>3. Demo returns to Step 1 (Customer Home)</li>
            </ol>
          </div>
        </div>
      )
    },
    {
      id: 'analytics',
      title: 'Demo Analytics',
      icon: BarChart3,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            Track investor demo completion rates and engagement metrics.
          </p>
          
          <div className="bg-slate-700/50 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Metrics Tracked:</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Total demo sessions</li>
              <li>• Completion rate</li>
              <li>• Average duration</li>
              <li>• Steps completed per session</li>
              <li>• Funnel drop-off by step</li>
              <li>• Guided vs Explore mode breakdown</li>
            </ul>
          </div>
          
          <div className="bg-indigo-900/30 border border-indigo-700/50 rounded-lg p-4">
            <h4 className="text-indigo-300 font-medium mb-2">How to Access:</h4>
            <ol className="text-sm text-slate-300 space-y-1">
              <li>1. From Admin Dashboard, click <strong>"Demo Analytics"</strong></li>
              <li>2. Or navigate to <code className="bg-slate-800 px-2 py-0.5 rounded">/admin/demo-analytics</code></li>
            </ol>
          </div>
          
          <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4">
            <h4 className="text-red-300 font-medium mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Data Isolation
            </h4>
            <p className="text-sm text-slate-300">
              Demo Analytics is <strong>strictly separated</strong> from production metrics.
              Simulated data is never mixed with real company performance.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'scenarios',
      title: 'Demo Scenarios',
      icon: CheckCircle2,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            Advanced scenarios you can demonstrate beyond the standard flow.
          </p>
          
          <div className="space-y-3">
            <div className="bg-slate-700/50 rounded-lg p-3">
              <h4 className="text-white font-medium text-sm mb-1">Decline Scenario</h4>
              <p className="text-xs text-slate-400">
                At Step 7 (Beneficiary Approval), explain that María can decline if the amount is wrong.
                The colmado must submit a new request with the correct amount.
              </p>
            </div>
            
            <div className="bg-slate-700/50 rounded-lg p-3">
              <h4 className="text-white font-medium text-sm mb-1">Expiration Scenario</h4>
              <p className="text-xs text-slate-400">
                Purchase requests expire after 15 minutes. María must respond promptly.
                Expired requests return funds to her available balance.
              </p>
            </div>
            
            <div className="bg-slate-700/50 rounded-lg p-3">
              <h4 className="text-white font-medium text-sm mb-1">Partial Use Scenario</h4>
              <p className="text-xs text-slate-400">
                María can make multiple smaller purchases from her $100 availability.
                After RD$2,850 purchase, she still has ~RD$3,150 remaining.
              </p>
            </div>
            
            <div className="bg-slate-700/50 rounded-lg p-3">
              <h4 className="text-white font-medium text-sm mb-1">QR Code Scenario</h4>
              <p className="text-xs text-slate-400">
                In Explore mode, show the beneficiary's QR code that colmados scan
                to look up their available balance.
              </p>
            </div>
          </div>
        </div>
      )
    }
  ];
  
  const toggleSection = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900 to-slate-800 p-6 border-b border-slate-700">
          <h2 className="text-2xl font-bold text-white mb-1">Owner Demo Guide</h2>
          <p className="text-slate-400 text-sm">Complete instructions for investor demonstrations</p>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {sections.map((section) => {
            const Icon = section.icon;
            const isExpanded = expandedSection === section.id;
            
            return (
              <div 
                key={section.id}
                className="bg-slate-700/30 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-purple-400" />
                    </div>
                    <span className="text-white font-medium">{section.title}</span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                
                {isExpanded && (
                  <div className="px-4 pb-4">
                    {section.content}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex justify-end">
          <Button
            onClick={onClose}
            className="bg-purple-600 hover:bg-purple-700"
          >
            Close Guide
          </Button>
        </div>
      </div>
    </div>
  );
}
